// apps/merchant-v3/src/lib/websocket/WsProvider.tsx
//
// Centralised realtime owner mounted at the AppShell root.
//
// D-281 (2026-07-01) — migrated off the divergent local `wsClient`
// (which spoke `{op}` + dot-channels and never matched the API WS server)
// onto the canonical `@printway/ui` `connectWs` client (ws-ticket auth,
// colon-channels `ws:user:{id}` / `ws:merchant:{id}`, `{type:'event',
// eventName, payload}` frames) — the same transport admin + vendor use.
//
// The provider owns ONE socket for the lifetime of the merchant session,
// subscribes the user + merchant channels, and re-exposes a per-EVENT
// subscription via `useWsEvent(eventName, handler)`. Consumers register by
// the BE event name (e.g. `notification.new`, `order.signal`) and get
// the canonical `WsServerEvent` ({ channel, eventName, payload, seq }).
//
// testids: ws-status-indicator (DevPanel-only, hidden in prod).
//
// vg-binding: P6.D-256
// vg-binding: P6.D-281
// vg-binding: PLAN:task-58
// vg-binding: G-08
// vg-binding: G-49
// vg-binding: G-52
// vg-binding: G-60
// vg-binding: CONTEXT:decisions           (P15.D-18 notification.new, P15.D-07 membership events; Task 25)
// vg-binding: INTERFACE-STANDARDS:error-shape

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { connectWs, type WsClientHandle, type WsServerEvent } from '@printway/ui';

import { useAuth } from '@/lib/auth/AuthProvider';
import { API_BASE_URL } from '@/lib/api/client';

type WsStatus = 'idle' | 'connecting' | 'connected' | 'disconnected';

type EventHandler = (ev: WsServerEvent) => void;

interface WsContextValue {
  status: WsStatus;
  /** Register a handler for a specific BE event name (e.g.
   *  `notification.new`, `order.signal`). Returns an unsubscribe
   *  function. */
  on: (eventName: string, handler: EventHandler) => () => void;
}

const WsContext = createContext<WsContextValue | null>(null);

function getMerchantId(user: {
  id?: string;
  role?: string;
  parentMerchantId?: string | null;
} | null): string | null {
  if (!user) return null;
  // Team members carry parentMerchantId pointing to the owning merchant.
  // Owners are themselves the merchant — their `id` is the merchant id at
  // the WS channel topology layer.
  if (user.role === 'team_member' && user.parentMerchantId) {
    return user.parentMerchantId;
  }
  return user.id ?? null;
}

interface WsProviderProps {
  children: ReactNode;
}

export function WsProvider({ children }: WsProviderProps): JSX.Element {
  const { user } = useAuth();
  const typedUser = user as unknown as {
    id?: string;
    role?: string;
    parentMerchantId?: string | null;
  } | null;
  const userId = typedUser?.id ?? null;
  const merchantId = useMemo<string | null>(
    () => getMerchantId(typedUser),
    [typedUser],
  );

  const handlersRef = useRef<Map<string, Set<EventHandler>>>(new Map());
  const handleRef = useRef<WsClientHandle | null>(null);
  // Set by the connect effect — installs a `handle.on(eventName)` relay for a
  // newly-seen event name. Null while disconnected; the effect back-fills
  // relays for every name already in handlersRef on (re)connect.
  const ensureRelayRef = useRef<((eventName: string) => void) | null>(null);
  const [status, setStatus] = useState<WsStatus>('idle');

  // Stable `on` — handlers stored by BE event name. The single canonical
  // socket fans every `type:'event'` frame to the matching set.
  const on = useCallback<WsContextValue['on']>((eventName, handler) => {
    let set = handlersRef.current.get(eventName);
    if (!set) {
      set = new Set();
      handlersRef.current.set(eventName, set);
    }
    set.add(handler);
    // Lazy relay — without this, events for names outside the pre-install
    // list were silently dropped (merchant never saw ticket.reply —
    // dbg-20260706-173837).
    ensureRelayRef.current?.(eventName);
    return (): void => {
      const cur = handlersRef.current.get(eventName);
      if (!cur) return;
      cur.delete(handler);
      if (cur.size === 0) handlersRef.current.delete(eventName);
    };
  }, []);

  useEffect(() => {
    if (!userId || !merchantId) {
      setStatus('idle');
      return;
    }
    setStatus('connecting');

    // Absolute ticket URL when the API lives on a different origin
    // (VITE_API_BASE_URL baked in for prod); relative for proxy dev.
    const ticketUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/v1/auth/ws-ticket`;
    const handle = connectWs({ scope: 'merchant', orgId: merchantId, ticketUrl });
    handleRef.current = handle;

    // user channel (notifications publish to ws:user:{userId}) + merchant
    // channel (order signals publish to ws:merchant:{merchantId}).
    handle.subscribe(`ws:user:${userId}`);
    handle.subscribe(`ws:merchant:${merchantId}`);

    // Single fanout listener — bridge every domain event to registered
    // per-event handlers. `handle.on` is eventName-scoped, so one relay is
    // installed per name: eagerly for the core names below, lazily (via
    // ensureRelayRef, called from `on`) for anything a consumer subscribes
    // to later (e.g. ticket.reply / ticket.created).
    const installed = new Set<string>();
    const offFns: Array<() => void> = [];
    const ensureRelay = (eventName: string): void => {
      if (installed.has(eventName)) return;
      installed.add(eventName);
      const off = handle.on(eventName, (ev) => {
        const set = handlersRef.current.get(ev.eventName);
        if (!set) return;
        for (const h of Array.from(set)) {
          try {
            h(ev);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[WsProvider] handler threw', err);
          }
        }
      });
      offFns.push(off);
    };
    ensureRelayRef.current = ensureRelay;
    // Pre-install relays for the core event names this app consumes.
    // P15.D-18: canonical `notification.new` (signal-only). The legacy
    // `notification.create` relay was REMOVED in Task 29 with the server-side
    // emitter (G-35). P15.D-07: `membership.revoked` / `membership.granted` ride
    // ws:user:{userId}.
    for (const name of [
      'notification.new',
      'notification.read',
      'membership.revoked',
      'membership.granted',
      'order.signal',
    ]) {
      ensureRelay(name);
    }
    // Back-fill relays for consumers that registered before the socket
    // connected (or across a reconnect remount).
    for (const name of handlersRef.current.keys()) {
      ensureRelay(name);
    }

    const offLifecycle = handle.onLifecycle((ev) => {
      if (ev.type === 'open') setStatus('connected');
      else if (ev.type === 'close') setStatus('disconnected');
    });

    return () => {
      ensureRelayRef.current = null;
      offLifecycle();
      for (const off of offFns) {
        try {
          off();
        } catch {
          /* ignore */
        }
      }
      try {
        handle.disconnect();
      } catch {
        /* ignore */
      }
      handleRef.current = null;
      setStatus('disconnected');
    };
  }, [userId, merchantId]);

  const value = useMemo<WsContextValue>(() => ({ status, on }), [status, on]);

  return (
    <WsContext.Provider value={value}>
      {children}
      {/* DevPanel-only indicator — hidden in production builds. */}
      {import.meta.env.DEV ? (
        <div
          data-testid="ws-status-indicator"
          data-ws-status={status}
          aria-hidden
          /* dark-ok: dev-only terminal-style status pill, rendered under import.meta.env.DEV */
          className="fixed bottom-2 left-2 rounded-full bg-slate-800 px-2 py-0.5 font-mono text-[10px] text-emerald-300 opacity-60"
        >
          ws:{status}
        </div>
      ) : null}
    </WsContext.Provider>
  );
}

export function useWs(): WsContextValue {
  const ctx = useContext(WsContext);
  if (!ctx) {
    throw new Error(
      'useWs / useWsEvent must be used inside a <WsProvider> — mount at the AppShell root.',
    );
  }
  return ctx;
}

/**
 * Subscribe to a specific BE event name for the lifetime of the calling
 * component. Pass a stable callback (useCallback) to avoid re-subscribing
 * on every render. `eventName = null` disables the subscription.
 *
 * @example
 * useWsEvent('order.signal', (ev) => { refetchOrders(); });
 */
export function useWsEvent(
  eventName: string | null,
  handler: EventHandler,
): void {
  const { on } = useWs();
  const handlerRef = useRef<EventHandler>(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);
  useEffect(() => {
    if (!eventName) return;
    const unsub = on(eventName, (ev) => handlerRef.current(ev));
    return () => unsub();
  }, [eventName, on]);
}
